import { useState } from 'react';
import { inject } from 'mobx-react';
import DragAndDrop from '../drag-drop';
import closeIcon from '../../assets/img/icon-close.svg';
import fileIcon from '../../assets/img/icon-file.svg';
import uploadIcon from '../../assets/img/icon-upload.svg';
import './upload-file-drag-and-drop.scss';
import InputAutocomplete from '../../components/input-autocomplete/input-autocomplete';
import { TRANSACTION_TYPES } from '../../pages/new-cc-invoice/new-cc-invoice';

/**
 * @typedef {Object} FileItem
 * @property {File} rawFile
 * @property {FormData} formData
 * @property {string} uploadString
 * @property {string[]} errors
 */
const UploadFileDragAndDrop = ({ data, submitFiles, statementDefaultTransactionType='expense_invoice' }) => {
  const [files, setFiles] = useState(/** @type {FileItem[]} */ []);

  const handleDrop = (droppedFiles) => {
    let fileList = [...files];
    let errors = [];

    const validFileTypes = ['application/pdf', 'image/png', 'image/jpeg'];
    const newValidFiles = Array.from(droppedFiles).filter((file) => {
      if (!file.name) return false;

      if (!validFileTypes.some((type) => file.type.includes(type))) {
        errors.push(data.t.read('invalid_upload_file_type'));
        return false;
      }

      return !fileList.some((existingFile) => existingFile.name === file.name && existingFile.size === file.size);
    });

    fileList = [...fileList, ...newValidFiles];

    updateFiles(fileList, errors);
  };

  const prepareFileForUpload = (fileToProcess, errors) => {
    if (fileToProcess.rawFile) return fileToProcess;

    const uploadString = fileToProcess.name + ' (' + (fileToProcess.size / 1000 / 1000).toFixed(2) + ' ' + data.t.read('mb_unit') + ')';
    const formData = new FormData();
    if (fileToProcess.type.includes('application/pdf')) {
      formData.append('pdf', fileToProcess);
    } else {
      formData.append('image', fileToProcess);
    }

    return { rawFile: fileToProcess, formData, uploadString, errors };
  };

  const updateFiles = (fileList, errors) => {
    setFiles(fileList.map((file) => prepareFileForUpload(file, errors)));
  };

  const removeFile = (fileToRemove) => {
    const updatedFiles = files.filter(({ rawFile }) => rawFile?.name !== fileToRemove?.name || rawFile?.size !== fileToRemove?.size);
    updateFiles(updatedFiles, []);
  };

  return (
    <DragAndDrop handleDrop={(droppedFiles) => handleDrop(droppedFiles)} data-testid='upload-file-drag-and-drop'>
      {files?.length > 0 ? (
        <div className='new-files-container' data-testid='files-container'>
          <div className='mb-5 full-width border-bottom' data-testid='file-list'>
            {files.map(({ rawFile }, index) => {
              return (
                <div
                  key={rawFile?.name + '-' + index}
                  className={`invoice-item d-flex justify-content-between pb-2 pt-3 ${index !== 0 ? 'border-top' : ''}`}
                  data-testid={`file-item-${index}`}
                >
                  <div className='invoice-file-info' data-testid={`file-info-${index}`}>
                    <div className='font-weight-bold text-truncate' data-testid={`file-name-${index}`} style={{maxWidth: '175px'}}>
                      {rawFile?.name}
                    </div>
                    <div className='font-small semi-faded' data-testid={`file-details-${index}`}>
                      {(rawFile?.size / 1000 / 1000).toFixed(2)} {data.t.read('mb_unit')}
                      <span className='ml-1' data-testid={`file-type-${index}`}>
                        {rawFile?.type.indexOf('application/pdf') !== -1
                          ? 'PDF'
                          : rawFile?.type.indexOf('image/png') !== -1
                          ? 'PNG'
                          : rawFile?.type.indexOf('image/jpeg') !== -1
                          ? 'JPG'
                          : 'N/A'}
                      </span>
                    </div>
                    <InputAutocomplete
                      options={TRANSACTION_TYPES.map((type) => ({ label: data.t.read(`transaction_type.${type}`), value: type }))}
                      onChange={(selected) => {
                        rawFile.transactionType = selected.value;
                      }}
                      placeholder={data.t.read('select_transaction_type')}
                      title={data.t.read('transaction_type.title')}
                      value={
                        rawFile.transactionType
                          ? { label: data.t.read(`transaction_type.${rawFile.transactionType}`), value: rawFile.transactionType }
                          : {
                              label: data.t.read(`transaction_type.${statementDefaultTransactionType}`),
                              value: statementDefaultTransactionType,
                            }
                      }
                    />
                  </div>
                  <img
                    src={closeIcon}
                    alt='Close'
                    className='ml-4 pointer'
                    onClick={() => removeFile(rawFile)}
                    data-testid={`remove-file-${index}`}
                  />
                </div>
              );
            })}
          </div>
          <div className='invoice-item p-0' data-testid='add-another-container'>
            <label className='btn btn-secondary p-3' style={{ width: '100%', height: '100%', display: 'block' }} data-testid='add-another-label'>
              <img src={fileIcon} alt='New invoice' className='mr-4' />
              {data.t.read('add_another_invoice')}
              <input
                type='file'
                style={{ display: 'none' }}
                multiple
                onChange={(evt) => handleDrop(evt.target.files)}
                data-testid='add-another-input'
              />
            </label>
          </div>
          <div className='mt-4'>
            <button
              className='btn btn-primary pl-5 pr-5'
              onClick={() => {
                submitFiles(files);
                setFiles([]);
              }}
              data-testid='submit-button'
            >
              {data.t.read('submit')}
            </button>
          </div>
        </div>
      ) : (
        <div className='first-files-container' data-testid='empty-container'>
          <div>
            <img src={uploadIcon} alt='XPENS logo' data-testid='upload-icon' />
          </div>
          <div className='font-weight-bold mt-3' data-testid='upload-title'>
            {data.t.read('upload_new_invoice')}
          </div>
          <div className='font-small semi-faded mb-5 browse-files' data-testid='browse-text'>
            {data.t.read('browse_files')}
          </div>
          <div>
            <label className='btn btn-primary pl-5 pr-5' data-testid='browse-label'>
              {data.t.read('browse')}
              <input type='file' style={{ display: 'none' }} multiple onChange={(evt) => handleDrop(evt.target.files)} data-testid='file-input' />
            </label>
          </div>
        </div>
      )}
    </DragAndDrop>
  );
};

export default inject('data')(UploadFileDragAndDrop);
